import React from 'react';
import { useTranslation } from 'react-i18next';
import PlanDetailHeader from '../../components/PlanDetailHeader';
import NameStatusSection from './components/NameStatusSection';
import LawReferenceSection from './components/LawReferenceSection';
import CaseSection from './components/CaseSection';
import DecisionSection from './components/DecisionSection';
import FormFooterActions from './components/FormFooterActions';
import type { PlanInfoFormValues, SelectOption } from '../../types';
import styles from './PlanInfoPage.module.css';

export type PlanInfoFieldErrors = Partial<Record<keyof PlanInfoFormValues, string>>;

interface PlanInfoPageProps {
    values: PlanInfoFormValues;
    fieldErrors?: PlanInfoFieldErrors;
    plantypeOptions: SelectOption[];
    planstatusOptions: SelectOption[];
    lovreferanseOptions: SelectOption[];
    forslagsstillerTypeOptions: SelectOption[];
    onChange: <K extends keyof PlanInfoFormValues>(field: K, value: PlanInfoFormValues[K]) => void;
    onCancel: () => void;
    onSave: () => void;
    saveDisabled?: boolean;
    isSaving?: boolean;
    /** Vises kun når brukeren har gjort endringer som ikke er lagret. */
    isDirty?: boolean;
}

const PlanInfoPage: React.FC<PlanInfoPageProps> = ({
    values,
    fieldErrors = {},
    plantypeOptions,
    planstatusOptions,
    lovreferanseOptions,
    forslagsstillerTypeOptions,
    onChange,
    onCancel,
    onSave,
    saveDisabled,
    isSaving,
    isDirty,
}) => {
    const { t } = useTranslation();

    return (
        <article className={styles.page}>
            <PlanDetailHeader title={t('planInfo.title')} subtitle={t('planInfo.subtitle')} />
            <NameStatusSection
                values={{
                    plannavn: values.plannavn,
                    plantype: values.plantype,
                    planstatus: values.planstatus,
                    planbestemmelser: values.planbestemmelser,
                }}
                errors={fieldErrors}
                plantypeOptions={plantypeOptions}
                planstatusOptions={planstatusOptions}
                onChange={onChange}
            />
            <LawReferenceSection
                values={{ lovreferanse: values.lovreferanse, beskrivelse: values.beskrivelse }}
                errors={fieldErrors}
                lovreferanseOptions={lovreferanseOptions}
                onChange={onChange}
            />
            <CaseSection
                values={{ saksnummer: values.saksnummer, forslagsstillerType: values.forslagsstillerType }}
                errors={fieldErrors}
                forslagsstillerTypeOptions={forslagsstillerTypeOptions}
                onChange={onChange}
            />
            <DecisionSection
                values={{
                    vedtaksdato: values.vedtaksdato,
                    vedtakEndeligPlanDato: values.vedtakEndeligPlanDato,
                    ikrafttredelsesdato: values.ikrafttredelsesdato,
                    kunngjoringsdato: values.kunngjoringsdato,
                    ubehandletKlage: values.ubehandletKlage,
                    ubehandletInnsigelse: values.ubehandletInnsigelse,
                }}
                errors={fieldErrors}
                onChange={onChange}
            />
            {isDirty && (
                <FormFooterActions
                    onCancel={onCancel}
                    onSave={onSave}
                    saveDisabled={saveDisabled}
                    isSaving={isSaving}
                />
            )}
        </article>
    );
};

export default PlanInfoPage;
